import { useState, useEffect, useCallback, useRef } from 'react';
import { SSE_CONNECTION_STATES, SSE_CONFIG } from '../Services/sseConstants';
import { 
  parseSSEEvent, 
  createEventHandlerMap, 
  registerEventHandler, 
  processSSEMessage 
} from '../Services/sseUtils';

/**
 * Custom hook for managing SSE connections with automatic reconnection and event handling
 * @param {string} clientId - Unique client identifier for SSE connection
 * @param {object} options - Configuration options
 * @returns {object} SSE connection state and controls
 */
export const useSSE = (clientId, options = {}) => {
  const {
    autoConnect = true,
    retryDelay = SSE_CONFIG.RETRY_DELAY,
    maxRetries = SSE_CONFIG.MAX_RETRY_ATTEMPTS,
    onConnect = null,
    onDisconnect = null,
    onError = null,
    onMessage = null,
    enableHeartbeat = true,
    heartbeatInterval = SSE_CONFIG.HEARTBEAT_INTERVAL,
    debug = false
  } = options;

  const [connectionState, setConnectionState] = useState(SSE_CONNECTION_STATES.DISCONNECTED);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const eventSourceRef = useRef(null);
  const handlerMapRef = useRef(createEventHandlerMap());
  const heartbeatTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const lastHeartbeatRef = useRef(Date.now());

  /**
   * Logs debug messages if debug mode is enabled
   */
  const log = useCallback((message, ...args) => {
    if (debug) {
      console.log(`[useSSE - ${clientId}] ${message}`, ...args);
    }
  }, [debug, clientId]);

  /**
   * Starts the heartbeat monitoring
   */
  const startHeartbeatMonitoring = useCallback(() => {
    if (!enableHeartbeat) return;

    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
    }

    heartbeatTimerRef.current = setInterval(() => {
      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;
      
      if (timeSinceLastHeartbeat > SSE_CONFIG.CONNECTION_TIMEOUT) {
        log('Heartbeat timeout detected, reconnecting...');
        disconnect();
        connect();
      }
    }, heartbeatInterval);
  }, [enableHeartbeat, heartbeatInterval]);

  /**
   * Stops the heartbeat monitoring
   */
  const stopHeartbeatMonitoring = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  /**
   * Handles incoming SSE messages
   */
  const handleMessage = useCallback((event) => {
    lastHeartbeatRef.current = Date.now();
    const data = event.data;

    log('Received message:', data);

    // Call the general onMessage callback if provided
    if (onMessage) {
      try {
        onMessage(data);
      } catch (error) {
        console.error('Error in onMessage callback:', error);
      }
    }

    // Process message through handler map
    processSSEMessage(data, handlerMapRef.current);
  }, [onMessage, log]);

  /**
   * Handles SSE errors
   */
  const handleError = useCallback((event) => {
    const error = {
      message: 'SSE connection error',
      event: event,
      timestamp: new Date()
    };

    log('Connection error:', error);
    setLastError(error);

    if (onError) {
      onError(error);
    }

    // Attempt reconnection if under retry limit
    if (retryCount < maxRetries) {
      setConnectionState(SSE_CONNECTION_STATES.RECONNECTING);
      const delay = retryDelay * Math.pow(2, retryCount); // Exponential backoff
      
      log(`Reconnecting in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);

      reconnectTimerRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        connect();
      }, delay);
    } else {
      log('Max retry attempts reached');
      setConnectionState(SSE_CONNECTION_STATES.ERROR);
      disconnect();
    }
  }, [retryCount, maxRetries, retryDelay, onError, log]);

  /**
   * Handles SSE connection open
   */
  const handleOpen = useCallback(() => {
    log('Connection established');
    setConnectionState(SSE_CONNECTION_STATES.CONNECTED);
    setIsConnected(true);
    setRetryCount(0);
    setLastError(null);
    lastHeartbeatRef.current = Date.now();

    if (onConnect) {
      onConnect();
    }

    startHeartbeatMonitoring();
  }, [onConnect, startHeartbeatMonitoring, log]);

  /**
   * Connects to the SSE endpoint
   */
  const connect = useCallback(() => {
    if (!clientId) {
      console.error('Cannot connect: clientId is required');
      return;
    }

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    log('Connecting...');
    setConnectionState(SSE_CONNECTION_STATES.CONNECTING);

    try {
      const eventSource = new EventSource(`/sse?clientId=${clientId}`);
      
      eventSource.onopen = handleOpen;
      eventSource.onmessage = handleMessage;
      eventSource.onerror = handleError;

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('Failed to create EventSource:', error);
      setConnectionState(SSE_CONNECTION_STATES.ERROR);
      setLastError({ message: error.message, timestamp: new Date() });
    }
  }, [clientId, handleOpen, handleMessage, handleError, log]);

  /**
   * Disconnects from the SSE endpoint
   */
  const disconnect = useCallback(() => {
    log('Disconnecting...');

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    stopHeartbeatMonitoring();

    setConnectionState(SSE_CONNECTION_STATES.DISCONNECTED);
    setIsConnected(false);

    if (onDisconnect) {
      onDisconnect();
    }
  }, [onDisconnect, stopHeartbeatMonitoring, log]);

  /**
   * Registers an event handler for specific event type(s)
   */
  const on = useCallback((eventType, handler) => {
    registerEventHandler(handlerMapRef.current, eventType, handler);
    
    // Return unsubscribe function
    return () => {
      const handlers = handlerMapRef.current.get(eventType);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }, []);

  /**
   * Removes all handlers for a specific event type
   */
  const off = useCallback((eventType) => {
    handlerMapRef.current.delete(eventType);
  }, []);

  /**
   * Resets retry count and attempts to reconnect
   */
  const reconnect = useCallback(() => {
    log('Manual reconnect requested');
    setRetryCount(0);
    disconnect();
    connect();
  }, [connect, disconnect, log]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect && clientId) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [clientId, autoConnect]); // Only reconnect if clientId changes

  return {
    // Connection state
    connectionState,
    isConnected,
    retryCount,
    lastError,

    // Controls
    connect,
    disconnect,
    reconnect,

    // Event handlers
    on,
    off,

    // Direct reference (use with caution)
    eventSource: eventSourceRef.current
  };
};

export default useSSE;
